import React from 'react';
import { ErrorMessageProps } from '../df-form-preview-interfaces';

export const DfFormErrorMsg: React.FC<ErrorMessageProps> = ({
  validationErrors,
  fieldId,
  touchedFields,
  formSubmitted,
  properties,
  localValidation,
  isTouched: localIsTouched,
  mode,
}) => {
  const getErrorMessage = (): string => {
    // Only show error messages in test mode
    if (mode !== 'test') {
      return '';
    }
    
    // Check if field is touched or form is submitted
    const isTouched = localIsTouched || touchedFields[fieldId] || formSubmitted;
    
    if (!isTouched) {
      return '';
    }

    // Prioritize local validation - if local validation is valid, don't show any errors
    if (localValidation && localValidation.isValid) {
      return '';
    }

    // Show local validation errors if validation failed
    if (localValidation && !localValidation.isValid && Object.keys(localValidation.errors).length > 0) {
      const errorKeys = Object.keys(localValidation.errors);
      if (errorKeys.length > 0) {
        const firstError = errorKeys[0];
        return getDefaultErrorMessage(firstError);
      }
    }

    // Only check external validation errors if local validation doesn't exist or is not valid
    const fieldErrors = validationErrors[fieldId];
    if (fieldErrors && typeof fieldErrors === 'string' && fieldErrors.trim() !== '') {
      // For date/time components, check if we should use mode-specific message
      const componentName = (properties as any).name || '';
      const isDateTimeComponent = componentName === 'date-picker' || componentName === 'datetime-picker';
      if (isDateTimeComponent && fieldErrors.includes('valid date')) {
        const dateTimeMode = (properties.basic as any)?.dateTimeMode || (componentName === 'datetime-picker' ? 'datetime' : 'date');
        if (dateTimeMode === 'time') {
          return 'Please select a valid time';
        } else if (dateTimeMode === 'datetime') {
          return 'Please select a valid date and time';
        }
      }
      return fieldErrors;
    }

    return '';
  };

  const getDefaultErrorMessage = (errorType: string): string => {
    const fieldLabel = properties.basic.label || 'This field';
    
    // Check if there's a custom validation message
    const customMessage = properties.validation.customValidationMessage;
    if (customMessage) {
      return customMessage;
    }
    
    // For date/time components, check dateTimeMode for appropriate error messages
    const componentName = (properties as any).name || '';
    const isDateTimeComponent = componentName === 'date-picker' || componentName === 'datetime-picker';
    const dateTimeMode = isDateTimeComponent ? ((properties.basic as any)?.dateTimeMode || (componentName === 'datetime-picker' ? 'datetime' : 'date')) : null;
    
    switch (errorType) {
      case 'required':
        return `${fieldLabel} is required`;
      case 'invalidDate':
        // Show mode-specific error messages for date/time components
        if (dateTimeMode === 'time') {
          return 'Please select a valid time';
        } else if (dateTimeMode === 'datetime') {
          return 'Please select a valid date and time';
        } else if (dateTimeMode === 'date') {
          return 'Please select a valid date';
        }
        return `${fieldLabel} is invalid`;
      case 'minLength':
        const minLength = properties.validation.minLength;
        return `${fieldLabel} must be at least ${minLength} characters long`;
      case 'maxLength':
        const maxLength = properties.validation.maxLength;
        return `${fieldLabel} must be no more than ${maxLength} characters long`;
      case 'min':
        const min = properties.validation.min;
        return `${fieldLabel} must be at least ${min}`;
      case 'max':
        const max = properties.validation.max;
        return `${fieldLabel} must be no more than ${max}`;
      case 'pattern':
        return `${fieldLabel} format is invalid`;
      case 'email':
        return `${fieldLabel} must be a valid email address`;
      default:
        return `${fieldLabel} is invalid`;
    }
  };

  const errorMessage = getErrorMessage();

  if (!errorMessage) {
    return null;
  }

  return (
    <div className="form-error-msg">
      {errorMessage}
    </div>
  );
};

export default DfFormErrorMsg;
